import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import PortalDocument from "../../../document/PortalDocument";
import DialogDefault from "../../parameter/PortalDialogDefault";
import Parameter from "../../parameter/Parameter";
import MimeType from "../../../document/mimetype/MimeType";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalToolboxPortfolioPortfolio} from "../../../../shared/models/PortalToolboxPortfolioPortfolio";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class PortfolioRemoveDialogDefinition extends PortalDialog<PortalToolboxPortfolioPortfolio,
	PortalDialogProperties<PortalToolboxPortfolioPortfolio>,
	PortalDialogState<PortalToolboxPortfolioPortfolio>> {
	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.portfolio, Functions.getPortfolioDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.PORTFOLIO_REMOVE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkPortfolio: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["portfolio-remove-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.portfolio.remove.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						<div className="dialog-message">
							<Translate {...this.props} data="portfolio.remove.message" namespace="dialog"/>
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-delete small"}/>
					<Translate {...this.props} data="portfolio.remove.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	protected renderFooter(): ReactNode {
		return (
			<div>
				<Button label={Translate.value("buttons.yes")} className="primary-button"
						icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
						disabled={!this.isValid()} onClick={() => this.submit()}/>
				<Button label={Translate.value("buttons.no")} icon="icon icon-close" onClick={() => this.hide()}
						className="dialog-button"/>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted;
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			portfolio: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.portfolio.remove", MessageType.SUCCESS);
			this.props.context.getPage().addMessage(message);
		}

		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.PORTFOLIO_REMOVE;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxPortfolioPortfolio> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const PortfolioRemoveDialog = React.memo(PortfolioRemoveDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxPortfolioPortfolio>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxPortfolioPortfolio>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default PortfolioRemoveDialog;